/**
 * Tutor-side participant access panel (NC-1101, Feat 2.0 #1090) — mounted on
 * RunDetailsPage (Übersicht tab). Three concerns:
 *  1. Teilnehmer-Links erzeugen/rotieren (POST participant-links; warns that
 *     rotation invalidates previously distributed links) + copy/print view.
 *  2. Projektdateien einsammeln (POST collect; active runs, 5-min lock).
 *  3. Eingesammelte Zips einsehen/herunterladen (GET project-files).
 */

import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ArchiveIcon from '@mui/icons-material/Archive';
import PrintIcon from '@mui/icons-material/Print';
import { runsApi, extractErrorMessage } from '../services/api';
import { CollectedProjectFile, ParticipantLink } from '../services/types';
import { formatDateSafe } from '../utils/dateFormat';

interface Props {
  runId: string;
  runStatus?: string;
  /** operator+ only — the parent gates rendering, this is a defensive echo. */
  canOperate: boolean;
}

export function ParticipantAccessPanel({ runId, runStatus, canOperate }: Props) {
  const [links, setLinks] = useState<ParticipantLink[] | null>(null);
  const [linksBusy, setLinksBusy] = useState(false);
  const [linksOpen, setLinksOpen] = useState(false);
  const [collectBusy, setCollectBusy] = useState(false);
  const [collectMsg, setCollectMsg] = useState<string | null>(null);
  const [files, setFiles] = useState<CollectedProjectFile[] | null>(null);
  const [filesBusy, setFilesBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateLinks = useCallback(async () => {
    // Always confirm: hashes from an earlier session may exist, and every
    // generation invalidates previously distributed links.
    if (
      !window.confirm(
        'Neue Teilnehmer-Links erzeugen? Bereits verteilte Links werden dadurch ungültig.'
      )
    ) {
      return;
    }
    setLinksBusy(true);
    setError(null);
    try {
      const res = await runsApi.participantLinks(runId);
      setLinks(res.links);
      setLinksOpen(true);
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setLinksBusy(false);
    }
  }, [runId]);

  const triggerCollect = useCallback(async () => {
    setCollectBusy(true);
    setError(null);
    setCollectMsg(null);
    try {
      const res = await runsApi.collectProjectFiles(runId);
      setCollectMsg(
        res.jobUrl
          ? 'Einsammlung gestartet — Ergebnis erscheint in wenigen Minuten unter "Eingesammelte Projektdateien".'
          : 'Einsammlung gestartet.'
      );
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setCollectBusy(false);
    }
  }, [runId]);

  const loadFiles = useCallback(async () => {
    setFilesBusy(true);
    setError(null);
    try {
      const res = await runsApi.projectFiles(runId);
      setFiles(res.files);
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setFilesBusy(false);
    }
  }, [runId]);

  const printLinks = useCallback(() => {
    window.print();
  }, []);

  if (!canOperate) return null;

  return (
    <Card data-testid="participant-access-panel">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Teilnehmer-Zugang (Teilnehmerseite)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Jeder Teilnehmer erhält einen persönlichen Link (gültig 5 Tage vor Beginn bis 7 Tage nach
          Ende): Zugangsdaten, RDP-Datei und nach der Schulung der Projektdateien-Download.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {collectMsg && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCollectMsg(null)}>
            {collectMsg}
          </Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button
            variant="contained"
            startIcon={linksBusy ? <CircularProgress size={18} /> : <LinkIcon />}
            disabled={linksBusy}
            onClick={generateLinks}
          >
            Teilnehmer-Links erzeugen
          </Button>
          <Tooltip
            title={
              runStatus === 'active'
                ? 'Kopiert C:\\Schulungsunterlagen\\Projektdateien aller VMs in den 7-Tage-Downloadbereich'
                : 'Nur bei aktivem Lauf möglich (VMs müssen laufen)'
            }
          >
            <span>
              <Button
                variant="outlined"
                startIcon={collectBusy ? <CircularProgress size={18} /> : <ArchiveIcon />}
                disabled={collectBusy || runStatus !== 'active'}
                onClick={triggerCollect}
              >
                Projektdateien einsammeln
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="text"
            startIcon={filesBusy ? <CircularProgress size={18} /> : <CloudDownloadIcon />}
            disabled={filesBusy}
            onClick={loadFiles}
          >
            Eingesammelte anzeigen
          </Button>
        </Stack>

        {files !== null && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Eingesammelte Projektdateien</Typography>
            {files.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Noch keine Projektdateien eingesammelt.
              </Typography>
            ) : (
              <List dense>
                {files.map((f) => (
                  <ListItem
                    key={f.sam}
                    secondaryAction={
                      <Link href={f.downloadUrl} target="_blank" rel="noopener">
                        Download
                      </Link>
                    }
                  >
                    <ListItemText
                      primary={`${f.sam} (${(f.sizeBytes / (1024 * 1024)).toFixed(1)} MB)`}
                      secondary={`Eingesammelt: ${formatDateSafe(f.collectedAt)} · VM: ${f.vmName}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}

        <Dialog open={linksOpen} onClose={() => setLinksOpen(false)} fullWidth maxWidth="md">
          <DialogTitle>Teilnehmer-Links</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Diese Links werden nur EINMAL angezeigt (es wird nur ein Hash gespeichert). Jetzt
              kopieren/drucken und verteilen. „Teilnehmer-Links erzeugen" macht alte Links ungültig.
            </Alert>
            <List dense>
              {(links ?? []).map((l) => (
                <ListItem
                  key={l.sam}
                  secondaryAction={
                    <Tooltip title="Link kopieren">
                      <IconButton
                        edge="end"
                        aria-label={`Link für ${l.displayName} kopieren`}
                        onClick={() => navigator.clipboard.writeText(l.url)}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemText
                    primary={l.displayName}
                    secondary={l.url}
                    secondaryTypographyProps={{ sx: { wordBreak: 'break-all' } }}
                  />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button startIcon={<PrintIcon />} onClick={printLinks}>
              Drucken
            </Button>
            <Button onClick={() => setLinksOpen(false)}>Schließen</Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}
